import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp } from "../../../utils/common";
import { useTranslation } from "react-i18next";
import useMainContentDimensions from "../../../utils/mainContentDimensions";

import {
  Card,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Paper as MuiPaper,
  Button,
  Typography,
  CardContent,
  Box,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import Toolbar from "../../../components/Toolbar";
import useWindowDimensions from "../../../utils/windowDimensions";
import useAuth from "../../../hooks/useAuth";
import useReport from "../../../hooks/useReports";
import AdvanceFilter from "./components/AdvanceFilter";
import {
  searchPayload,
  convertedValue,
  formatDate,
  convertToDateString,
} from "../../../utils/common";
import moment from "moment";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

function StaticIpReport(props) {
  const { user } = useAuth();

  const {
    getStaticIpReports,
    staticIpReports,
    paging,
    downloadStaticIpReport,
    downloadFileType,
    isLoading,
    isLoadingDownload,
  } = useReport();

  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;

  const { width: mainContentWidth } = useMainContentDimensions();

  const { state } = useLocation();
  const { t } = useTranslation();

  const { radUserId } = props;

  const today = formatDate(new Date());

  const currentPayload = {
    filters: [],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [
      {
        field: "createdAt",
        sort: "DESC",
      },
    ],
  };

  const getBaseFilters = () => {
    let filters = [];
    if (radUserId !== undefined) {
      filters.push({
        fieldName: "radUserId",
        fieldValue: radUserId,
        op: "eq",
        dataType: "int",
        condition: "and",
        isJoin: true,
        attributeName: "radUser",
        entityName: "RadUserEntity",
      });
    } else if (user?.businessType === "USER") {
      filters.push({
        fieldName: "radUserId",
        fieldValue: user.radUserId,
        op: "eq",
        dataType: "int",
        condition: "and",
        isJoin: true,
        attributeName: "radUser",
        entityName: "RadUserEntity",
      });
    } else if (user?.businessType !== "SUPER_ADMIN" && user?.tenantId) {
      filters.push({
        fieldName: "tenantId",
        fieldValue: user.tenantId,
        op: "eq",
        dataType: "long",
        condition: "and",
        isJoin: true,
        attributeName: "radUser",
        entityName: "RadUserEntity",
      });
    }
    return filters;
  };

  useEffect(() => {
    if (user != undefined) {
      setPayload({
        ...currentPayload,
        filters: getBaseFilters(),
      });

      setPaginationModel({
        page: 0,
        pageSize: 100,
      });
    }
  }, [user, radUserId]);

  const [sortModel, setSortModel] = useState([
    {
      field: "updatedAt",
      sort: "desc",
    },
  ]);
  const [filterFormData, setFilterFormData] = useState({
    userName: "",
    staticIpAddress: "",
    createdAt: null,
    createdAtStr: "",
  });
  const { height } = useWindowDimensions();

  const { openDialog } = useDialog();

  const [payload, setPayload] = useState({});

  const [filterResetFunc, setFilterResetFunc] = useState(
    () => () => console.log("Refresh initialized"),
  );

  const fetchData = async () => {
    if (Object.keys(payload).length != 0) {
      await getStaticIpReports(payload);
    }
  };

  const refreshPage = () => {
    setPayload({
      ...currentPayload,
      filters: getBaseFilters(),
    });
    setFilterFormData({
      userName: "",
      staticIpAddress: "",
      createdAt: null,
      createdAtStr: "",
    });
    filterResetFunc();
    setPaginationModel({
      page: 0,
      pageSize: 100,
    });
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");

    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: newPaginationModel.pageSize, // Update pageSize dynamically
    };

    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  const paginationMetaRef = useRef();

  // Memoize to avoid flickering when the `hasNextPage` is `undefined` during refetch
  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  const onPaginationModelChange = (newModel) => {
    setPaginationModel(newModel);

    const pagination = {
      pageNumber: newModel.page + 1, // API expects 1-based
      pageSize: newModel.pageSize,
    };

    setPayload((prev) => ({
      ...prev,
      pagination,
    }));
  };

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Reports</Typography>
        <Typography>Static IP Reports</Typography>
      </Breadcrumbs>
    );
  };

  useEffect(() => {
    fetchData();
  }, [payload]);

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  const columns = [
    {
      field: "staticIpAddress",
      headerName: "Static IP Address",
      width: 150,
    },
    {
      field: "userName",
      headerName: "UserName",
      width: 150,
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 150,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 150,
    },
  ];

  const openFilterDialog = () => {
    return openDialog(
      "advance_filter",
      "Advance Filter",
      <AdvanceFilter
        payload={payload}
        setPayload={setPayload}
        setFilterResetFunc={setFilterResetFunc}
        filterFormData={filterFormData}
        setFilterFormData={setFilterFormData}
      />,
    );
  };
  const exportFile = async (fileType) => {
    await downloadStaticIpReport(fileType, payload);
  };

  return (
    <Card sx={{ width: "100%", maxWidth: mainContentWidth - 64 }}>
      <CardHeader
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack on small screens
          justifyContent: "space-between",
          alignItems: "flex-start", // Adjust alignment as needed
        }}
        action={
          <Toolbar
            downloadFile={exportFile}
            refreshPage={refreshPage}
            openFilterDialog={openFilterDialog}
            isLoadingDownload={isLoadingDownload}
            downloadFileType={downloadFileType}
          />
        }
        title={
          <Typography variant="h6" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
            {t("static_ip_reports")}
          </Typography>
        }
        subheader={<Box sx={{ display: "flex", flexWrap: "wrap" }}>{breadCrump()}</Box>}
      />
      <CardContent sx={{ height: height - 180 }}>
        <DataGrid
          columns={columns}
          rows={staticIpReports.data}
          getRowId={(row) => row["staticIpReportId"]}
          rowCount={staticIpReports.paging.totalNumberOfRecords}
          loading={isLoading}
          rowsPerPageOptions={[5]}
          pagination
          page={staticIpReports.paging.pageNumber}
          pageSize={staticIpReports.paging.pageSize}
          paginationMode="server"
          onPaginationModelChange={onPageChange}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          paginationMeta={paginationMeta}
          paging={{
            pageNumber: staticIpReports.paging.pageNumber,
            pageSize: staticIpReports.paging.pageSize,
          }}
          paginationModel={paginationModel}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
          }
          sx={{
            "& .MuiDataGrid-row.even-row": {
              backgroundColor: "#f9f9f9", // Light gray for even rows
            },
            "& .MuiDataGrid-row.odd-row": {
              backgroundColor: "#ffffff", // White for odd rows
            },
          }}
        />
      </CardContent>
    </Card>
  );
}

export default StaticIpReport;
